// src/utils/dateUtils.js

/**
 * Convert various date formats to a JavaScript Date object
 * @param {any} dateValue - Date value that could be Firestore Timestamp, ISO string, or Date object
 * @returns {Date|null} - JavaScript Date object or null if invalid
 */
export const convertToDate = (dateValue) => {
  if (!dateValue) return null;

  let date = null;

  // Handle Firestore Timestamp
  if (dateValue && typeof dateValue === 'object' && dateValue.seconds) {
    date = new Date(dateValue.seconds * 1000);
  }
  // Handle Firestore toDate() method
  else if (dateValue && typeof dateValue === 'object' && typeof dateValue.toDate === 'function') {
    date = dateValue.toDate();
  }
  // Handle ISO string
  else if (typeof dateValue === 'string') {
    date = new Date(dateValue);
  }
  // Handle numeric timestamp
  else if (typeof dateValue === 'number') {
    date = new Date(dateValue);
  }
  // Already a Date object
  else if (dateValue instanceof Date) {
    date = dateValue;
  }

  // Return null if date is invalid
  if (!date || isNaN(date.getTime())) {
    return null;
  }

  return date;
};

/**
 * Format date for display in Spanish locale
 * @param {any} dateValue - Date value in any supported format
 * @returns {string} - Formatted date string or '-' if invalid
 */
export const formatDateForDisplay = (dateValue) => {
  const date = convertToDate(dateValue);
  return date ? date.toLocaleDateString('es-ES') : '-';
};

/**
 * Convert current date to ISO string for storage
 * @returns {string} - ISO string representation of current date
 */
export const getCurrentDateISO = () => {
  return new Date().toISOString();
};

/**
 * Format date in month/year format (mes/año)
 * @param {any} dateValue - Date value in any supported format
 * @returns {string} - Formatted date string in MM/YYYY format or '-' if invalid
 */
export const formatDateMonthYear = (dateValue) => {
  const date = convertToDate(dateValue);
  if (!date) return '-';
  
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${year}`;
};

/**
 * Get current time in Madrid timezone (hour and minutes)
 * @returns {object} - Object with hour and minutes in Madrid timezone
 */
export const getMadridTime = () => {
  const now = new Date();
  // Madrid is in Europe/Madrid timezone (CET/CEST)
  // Use Intl.DateTimeFormat to get the time in Madrid timezone
  const madridFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Madrid',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  
  const parts = madridFormatter.formatToParts(now);
  const hour = parts.find(part => part.type === 'hour').value.padStart(2, '0');
  const minutes = parts.find(part => part.type === 'minute').value.padStart(2, '0');
  
  return {
    hour,
    minutes,
    timeString: `${hour}:${minutes}`,
  };
};

/**
 * Get user IP address using a public API service
 * @returns {Promise<string>} - User's IP address or 'unknown' if failed
 */
export const getUserIP = async () => {
  try {
    // Try multiple IP services for reliability
    const services = [
      { url: 'https://api.ipify.org?format=json', type: 'json' },
      { url: 'https://api.ip.sb/ip', type: 'text' },
      { url: 'https://api.ipify.org?format=text', type: 'text' },
      { url: 'https://ipapi.co/json/', type: 'json' },
    ];

    for (const service of services) {
      try {
        const response = await fetch(service.url, {
          method: 'GET',
          headers: {
            'Accept': service.type === 'json' ? 'application/json' : 'text/plain',
          },
        });
        
        if (response.ok) {
          if (service.type === 'json') {
            const data = await response.json();
            // Different services return different formats
            if (data.ip) {
              return data.ip;
            }
          } else {
            // Plain text response
            const text = await response.text();
            const ip = text.trim();
            // Basic IP validation (IPv4 or IPv6)
            if (ip && (ip.includes('.') || ip.includes(':'))) {
              return ip;
            }
          }
        }
      } catch (error) {
        console.warn(`Failed to get IP from ${service.url}:`, error);
        continue;
      }
    }
    
    return 'unknown';
  } catch (error) {
    console.error('Error getting user IP:', error);
    return 'unknown';
  }
}; 